import { CourseDataQuantityType, CourseCategoryType, EventType, FormReservationType, OrderSummaryDataType, CourseDataType } from '../types'
import { bookingFeatures } from '../configs/features'
import dayjs from 'dayjs'
import crypto from 'crypto-js'
import { htmlDecode } from './index'

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[]
  }
}

const windowUserDisplayname = window.user_displayname ?? ''
const windowUserEmail = window.user_email ?? ''

export const pushDataLayerAddToCart = (courseDataQuantity: CourseDataQuantityType[], courseCategories: CourseCategoryType[], isAdmin: boolean) => {
  if (!bookingFeatures.gtm_datalayer) return

  window.dataLayer = window.dataLayer || []
  const items = courseDataQuantity
    .filter(course => course.quantity > 0)
    .map(course => {
      const category = courseCategories.find(cat => cat.course_ids.includes(course.id))
      return {
        item_id: course.id,
        item_name: course.name,
        item_category: category?.name || '',
        price: course.price,
        quantity: course.quantity
      }
    })

  const totalValue = items.reduce((sum, item) => sum + (item.price * item.quantity), 0)

  const dataLayerPayload = {
    event: "add_to_cart",
    admin_name: isAdmin ? windowUserDisplayname : "",
    // admin_email: isAdmin ? windowUserEmail : "",
    ecommerce: {
      currency: "THB",
      value: totalValue,
      items
    }
  }

  return window.dataLayer.push(dataLayerPayload)
}

export const pushDataLayerDatetimeSelect = (eventData: EventType) => {
  if (!bookingFeatures.gtm_datalayer) return

  window.dataLayer = window.dataLayer || []

  // Assuming `eventData.periods[0]` has the time range, and `periodStart` includes the date.
  const firstPeriod = eventData.periods?.[0]
  if (!firstPeriod) return

  const startDate = dayjs(firstPeriod.periodStart, 'YYYY-MM-DD HH:mm:ss')
  const startTime = startDate.format('HH:mm')
  const endTime = dayjs(firstPeriod.periodEnd, 'YYYY-MM-DD HH:mm:ss').format('HH:mm')

  const dataLayerPayload = {
    event: "datetime_select",
    booking: {
      date: startDate.format('YYYY-MM-DD'),
      time: `${startTime}-${endTime}`
    }
  }

  return window.dataLayer.push(dataLayerPayload)
}

export const pushDataLayerCustomerDetailEntered = (formData: FormReservationType) => {
  if (!bookingFeatures.gtm_datalayer) return

  window.dataLayer = window.dataLayer || []

  // Hash the email or phone number for the unique customer ID
  const customerIdentifier = formData.email && formData.phone ? `${formData.email}${formData.phone}` : ''
  const hashedCustomerId = customerIdentifier ? crypto.SHA256(customerIdentifier).toString() : ''

  // Determine if notes were added based on the checkboxes in formData
  const isNotesAdded = !!(formData.additional_request || formData.special_occasion || formData.dietary_restriction)

  const dataLayerPayload = {
    event: "customer_detail_entered",
    customer_info: {
      customer_id: hashedCustomerId,
      notes_added: isNotesAdded
    }
  }

  return window.dataLayer.push(dataLayerPayload)
}

export const pushDataLayerBeginCheckout = (orderSummaryData: OrderSummaryDataType, coursesData: CourseDataType[], courseCategories: CourseCategoryType[]) => {
  if (!bookingFeatures.gtm_datalayer) return

  window.dataLayer = window.dataLayer || []

  // Hash the email and phone number for the unique customer ID
  const customerIdentifier = orderSummaryData.email && orderSummaryData.phone ? `${orderSummaryData.email}${orderSummaryData.phone}` : ''
  const hashedCustomerId = customerIdentifier ? crypto.SHA256(customerIdentifier).toString() : ''

  const startDateTime = dayjs(orderSummaryData.start_date_time, 'YYYY-MM-DD HH:mm')
  const endDateTime = dayjs(orderSummaryData.end_date_time, 'YYYY-MM-DD HH:mm')

  const items = orderSummaryData.tickets
    .filter(ticket => ticket.persons > 0)
    .map(ticket => {
      const course = coursesData.find(c => htmlDecode(c.name).toLowerCase() === ticket.name.toLowerCase() || htmlDecode(c.displayName || '').toLowerCase() === ticket.name.toLowerCase())
      const category = course ? courseCategories.find(cat => cat.course_ids.includes(course.id)) : undefined

      return {
        item_id: course ? course.id : '',
        item_name: ticket.name,
        item_category: category?.name || '',
        price: ticket.price,
        quantity: ticket.persons
      }
    })

  const dataLayerPayload = {
    event: "begin_checkout",
    ecommerce: {
      currency: "THB",
      value: orderSummaryData.total_amount,
      booking_date: startDateTime.format('YYYY-MM-DD'),
      booking_time: `${startDateTime.format('HH:mm')}-${endDateTime.format('HH:mm')}`,
      customer_id: hashedCustomerId,
      items
    }
  }

  return window.dataLayer.push(dataLayerPayload)
}